import { Fragment, useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { pdApi, type PdOpportunity } from '../api';
import { HelpPanel } from '../HelpPanel';
import { SCORING_QUEUE } from '../helpContent';
import './ScoringQueue.css';

/**
 * Opportunities → Scoring Queue.
 *
 * CLONED from `/Users/glucic/src/ai_readiness_template/views/opportunities-scoring-queue.ejs`.
 * Its project-grouped tree, its filters, its column set, its wording and its empty state.
 *
 * WHY IT WAS REBUILT. The first port served the queue, in-progress and completed screens
 * from ONE flat table with a `mode` prop. §1.2.1 forbids that — *"Do not collapse several
 * source screens into one parameterised component because they look similar"* — and here
 * they genuinely are not similar. The queue groups by project and opens each to reveal its
 * opportunities; it filters on project and opportunity; it shows Owner and Submitted. None
 * of that survived the flattening. The owner: *"it's not the same as it is done on AI
 * readiness screen."*
 *
 * ── THE TREE ───────────────────────────────────────────────────────────────────────────
 *
 *   📁 PROJECT          blue row, click anywhere to toggle, "N Opportunities" on the right
 *      📂 opportunity   grey row, indented, Owner / Submitted / ▶️ Score / 👁️ View
 *
 * Projects start COLLAPSED, exactly as the source does — its opportunity rows carry the
 * `collapsed` class on first render. Expand All / Collapse All drive every project at once.
 *
 * ── WHAT COUNTS AS "IN THE QUEUE" ──────────────────────────────────────────────────────
 *
 * The server's rule, which is the source's (routes/index.js:6624): status `Ready for
 * Scoring`, OR `Scoring in Progress` with ZERO answers recorded. That second clause is the
 * subtle one — opening a scoring form flips an opportunity to "in progress" whether or not
 * anything was answered, so without it a merely-opened opportunity would vanish from the
 * queue while appearing on the in-progress screen with nothing in it.
 *
 * The filters run over the rendered rows and keep a project visible when either its own id
 * matches or any of its opportunities do — the source's `filterTable()` behaviour.
 */

type Row = PdOpportunity & Record<string, unknown>;

interface ProjectGroup {
  projectId: string;
  description: string;
  opportunities: Row[];
}

const s = (v: unknown) => (v === null || v === undefined ? '' : String(v));

/** Source :173 — `new Date(...).toLocaleDateString()`, with a blank for a missing date. */
const shortDate = (v: unknown) => (v ? new Date(String(v)).toLocaleDateString() : '');

export function ScoringQueue() {
  const navigate = useNavigate();

  const [rows, setRows] = useState<Row[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  /** Which projects are OPEN. Empty = all collapsed, which is the source's initial state. */
  const [open, setOpen] = useState<Set<string>>(new Set());
  const [filterProject, setFilterProject] = useState('');
  const [filterOpportunity, setFilterOpportunity] = useState('');

  const [rootEl, setRootEl] = useState<HTMLDivElement | null>(null);
  const [rootH, setRootH] = useState<number | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await pdApi.scoring.queue();
      setRows((data.opportunities as Row[]) ?? []);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  /* The shell never bounds its content area, so the height is measured rather than set to
     100% — see CLAUDE.md, "A screen header never scrolls". A callback ref is used because
     the table mounts after the loading gate, and an effect keyed on a `useRef` would run
     once against null and silently never apply a height. */
  useEffect(() => {
    if (!rootEl) return;
    const main = rootEl.closest('main');
    const measure = () => {
      const top = rootEl.getBoundingClientRect().top;
      const inset = main ? parseFloat(getComputedStyle(main).paddingBottom) || 0 : 0;
      const next = Math.max(320, Math.floor(window.innerHeight - top - inset));
      setRootH((prev) => (prev !== null && Math.abs(prev - next) < 2 ? prev : next));
    };
    measure();
    window.addEventListener('resize', measure);
    const ro = new ResizeObserver(measure);
    if (main) ro.observe(main);
    return () => {
      window.removeEventListener('resize', measure);
      ro.disconnect();
    };
  }, [rootEl]);

  /* Source :120 — group by project, "No Project" for the unassigned, keys sorted. */
  const groups = useMemo<ProjectGroup[]>(() => {
    const byProject = new Map<string, ProjectGroup>();
    for (const o of rows) {
      const projectId = s(o.project_id) || 'No Project';
      let g = byProject.get(projectId);
      if (!g) {
        g = { projectId, description: s(o.project_description), opportunities: [] };
        byProject.set(projectId, g);
      }
      g.opportunities.push(o);
    }
    return [...byProject.values()].sort((a, b) => a.projectId.localeCompare(b.projectId));
  }, [rows]);

  /* Source :406 — a project survives the filter if its own id matches AND at least one of
     its opportunities matches. Both filters are case-insensitive substring tests. */
  const visible = useMemo(() => {
    const p = filterProject.trim().toLowerCase();
    const q = filterOpportunity.trim().toLowerCase();
    return groups
      .map((g) => ({
        ...g,
        opportunities: q
          ? g.opportunities.filter(
              (o) =>
                s(o.opportunity_id).toLowerCase().includes(q) ||
                s(o.description).toLowerCase().includes(q),
            )
          : g.opportunities,
      }))
      .filter((g) => (p ? g.projectId.toLowerCase().includes(p) : true))
      .filter((g) => g.opportunities.length > 0);
  }, [groups, filterProject, filterOpportunity]);

  const visibleCount = visible.reduce((n, g) => n + g.opportunities.length, 0);

  const toggle = (projectId: string) =>
    setOpen((cur) => {
      const next = new Set(cur);
      if (next.has(projectId)) next.delete(projectId);
      else next.add(projectId);
      return next;
    });

  const expandAll = () => setOpen(new Set(groups.map((g) => g.projectId)));
  const collapseAll = () => setOpen(new Set());
  const clearFilters = () => {
    setFilterProject('');
    setFilterOpportunity('');
  };

  const scorePath = (id: string) =>
    `/portfolio_definition/opportunities/scoring-queue/${encodeURIComponent(id)}`;
  const viewPath = (id: string) =>
    `/portfolio_definition/opportunities/opportunity-registry/${encodeURIComponent(id)}`;

  return (
    <div
      className="sq-page"
      ref={setRootEl}
      style={rootH === null ? undefined : { height: `${rootH}px` }}
    >
      <div className="sq-fixed">
        <div className="sq-header">
          <div>
            <h1 className="sq-title">Scoring Queue</h1>
            <p className="sq-subtitle">Opportunities ready for scoring</p>
          </div>
          <HelpPanel help={SCORING_QUEUE} />
        </div>

        {/* Source :58 */}
        <div className="sq-quick">
          <button type="button" title="Expand All" onClick={expandAll}>
            ⬇️ Expand All
          </button>
          <button type="button" title="Collapse All" onClick={collapseAll}>
            ⬆️ Collapse All
          </button>
        </div>

        {/* Source :69 — the filter card appears only when there is something to filter. */}
        {rows.length > 0 ? (
          <div className="sq-filters">
            <div className="sq-filter">
              <label htmlFor="sq-fp">Project:</label>
              <input
                id="sq-fp"
                type="text"
                placeholder="Filter..."
                value={filterProject}
                onChange={(e) => setFilterProject(e.target.value)}
              />
            </div>
            <div className="sq-filter">
              <label htmlFor="sq-fo">Opportunity:</label>
              <input
                id="sq-fo"
                type="text"
                placeholder="Filter..."
                value={filterOpportunity}
                onChange={(e) => setFilterOpportunity(e.target.value)}
              />
            </div>
            <button type="button" className="sq-clear" onClick={clearFilters}>
              Clear
            </button>
            <span className="sq-count">
              {visibleCount} of {rows.length} opportunities
            </span>
          </div>
        ) : null}
      </div>

      <div className="sq-body">
        {error ? <div className="sq-error">{error}</div> : null}

        <div className="sq-card">
          {loading ? (
            <div className="sq-empty">
              <p>Loading…</p>
            </div>
          ) : rows.length === 0 ? (
            /* Source :95 — wording preserved exactly. */
            <div className="sq-empty">
              <div className="sq-empty-icon">📋</div>
              <h3>No Opportunities in Queue</h3>
              <p>Opportunities must be sent to scoring before they appear here</p>
            </div>
          ) : (
            <>
              {/*
               * The header is its OWN table above the scrolling one — the source's
               * structure (:101). Both carry `table-layout: fixed` with identical widths,
               * which is what keeps the columns aligned across the two elements.
               */}
              <div className="sq-thead-wrap">
                <table className="sq-table">
                  <thead>
                    <tr>
                      <th style={{ width: 40 }} />
                      <th style={{ width: 300 }}>Project / Opportunity</th>
                      <th style={{ width: 350 }}>Description</th>
                      <th style={{ width: 120, textAlign: 'center' }}>Owner</th>
                      <th style={{ width: 120, textAlign: 'center' }}>Submitted</th>
                      <th style={{ width: 120, textAlign: 'center' }}>Actions</th>
                    </tr>
                  </thead>
                </table>
              </div>

              <div className="sq-tbody-wrap">
                <table className="sq-table">
                  <tbody>
                    {visible.map((g) => {
                      const isOpen = open.has(g.projectId);
                      const n = g.opportunities.length;
                      return (
                        /* The KEY belongs on the fragment, not the inner <tr>: the fragment
                           is what this map returns. On the <tr> React still warns and, worse,
                           re-creates rows on reorder instead of moving them. */
                        <Fragment key={g.projectId}>
                          {/* Level 1 — the project. Source :138. */}
                          <tr
                            className="sq-project-row"
                            onClick={() => toggle(g.projectId)}
                          >
                            <td style={{ width: 40 }}>
                              <span className="sq-expand">{isOpen ? '▼' : '▶'}</span>
                            </td>
                            <td style={{ width: 300, fontSize: 12 }}>
                              📁 <strong>{g.projectId}</strong>
                            </td>
                            <td className="sq-cell-ellipsis" style={{ width: 350, fontSize: 11 }}>
                              {g.description}
                            </td>
                            <td style={{ width: 120 }} />
                            <td style={{ width: 120 }} />
                            <td className="sq-cell-center" style={{ width: 120, fontSize: 11 }}>
                              {n} {n === 1 ? 'Opportunity' : 'Opportunities'}
                            </td>
                          </tr>

                          {/* Level 2 — its opportunities. Source :157. */}
                          {isOpen
                            ? g.opportunities.map((o) => (
                                <tr key={s(o.opportunity_id)} className="sq-opportunity-row">
                                  <td style={{ width: 40 }} />
                                  <td
                                    className="sq-cell-indent"
                                    style={{ width: 300, fontSize: 11 }}
                                  >
                                    📂 <strong>{s(o.opportunity_id)}</strong>
                                  </td>
                                  <td
                                    className="sq-cell-ellipsis"
                                    style={{ width: 350, fontSize: 11 }}
                                  >
                                    {s(o.description)}
                                  </td>
                                  <td
                                    className="sq-cell-center"
                                    style={{ width: 120, fontSize: 11 }}
                                  >
                                    {s(o.owner_name)}
                                  </td>
                                  <td
                                    className="sq-cell-center sq-cell-muted"
                                    style={{ width: 120, fontSize: 11 }}
                                  >
                                    {shortDate(o.submitted_date)}
                                  </td>
                                  <td className="sq-cell-center" style={{ width: 120 }}>
                                    <div className="sq-actions">
                                      <button
                                        type="button"
                                        className="sq-icon-btn"
                                        title="Start Scoring"
                                        onClick={() => navigate(scorePath(s(o.opportunity_id)))}
                                      >
                                        ▶️
                                      </button>
                                      <button
                                        type="button"
                                        className="sq-icon-btn"
                                        title="View Details"
                                        onClick={() => navigate(viewPath(s(o.opportunity_id)))}
                                      >
                                        👁️
                                      </button>
                                    </div>
                                  </td>
                                </tr>
                              ))
                            : null}
                        </Fragment>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
